/**
 * Parse the VITE_ENABLED_PAGES environment variable into a Set of enabled page IDs.
 * When the variable is empty or unset, all pages are enabled (backwards-compatible default).
 */
const getEnabledPages = (): Set<string> => {
  const raw: string =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ENABLED_PAGES) || '';
  const pages = raw
    .split(',')
    .map((p: string) => p.trim().toLowerCase())
    .filter(Boolean);
  return new Set(pages);
};

const enabledPages = getEnabledPages();

/**
 * Check if a page is enabled via the VITE_ENABLED_PAGES env var.
 * If no pages are specified all pages are enabled (backwards-compatible).
 *
 * @param pageId - camelCase or lowercase page identifier
 */
export const isPageEnabled = (pageId: string): boolean => {
  if (enabledPages.size === 0) return true;
  return enabledPages.has(pageId.toLowerCase());
};

/**
 * Filter a routes array down to only the enabled pages.
 *
 * @param routes - Array of route objects with an optional `pageId` property
 */
export const filterEnabledRoutes = <T extends { pageId?: string }>(routes: T[]): T[] =>
  routes.filter((route) => !route.pageId || isPageEnabled(route.pageId));

/**
 * Check whether a sidebar navigation item is enabled based on:
 * 1. The VITE_ENABLED_PAGES feature flag (keyed via navIdToPageId).
 * 2. The user's entitlements returned by the /me endpoint.
 *
 * @param navigationId - kebab-case sidebar nav ID (e.g., 'order-hub')
 * @param navIdToPageId - map of navId → pageId built by createPortalConfig()
 * @param entitlements - user's feResourceCode entitlements.
 *   Pass `undefined` while loading to show all items.
 *   Pass an empty array to hide all gated items.
 */
export const isNavigationEnabled = (
  navigationId: string,
  navIdToPageId: Record<string, string>,
  entitlements?: string[]
): boolean => {
  const pageId = navIdToPageId[navigationId];
  if (pageId && !isPageEnabled(pageId)) return false;
  if (entitlements === undefined) return true;
  return entitlements.includes(navigationId);
};
